import {
  ConsignmentApiModel,
  Customer,
  Dimensions,
  ExtraBase,
  MarketplaceOrderApiModel,
  Meta,
  Owner,
  Service,
  SmartSendItemApiModel
} from '~/lib/api/smartsend/types';
import { SumNumber } from './ArrayHelper';

/** Loose comparison retained deliberately - socket payloads deliver numbers as strings. */
const dimensionsDiffer = (a: Dimensions, b: Dimensions) =>
  a?.Weight != b?.Weight || a?.Height != b?.Height || a?.Width != b?.Width || a?.Length != b?.Length;

export function DimensionChangeShouldTriggerQuote(item: SmartSendItemApiModel, dims: Dimensions) {
  return dimensionsDiffer(item.PackageDimensions, dims);
}

/**
 * A consignment arriving over the socket requotes only when it changes something the PRICE depends
 * on - the number of parcels, or any parcel's dimensions. Editing a customs description, SKU or
 * tariff code does not move the price and should not throw away a valid quote.
 *
 * Deliberately NOT `ConsignmentHasChanged`, which JSON-compares the whole object: that includes the
 * per-parcel UIDs, and `AddIdsToConsignment` regenerates every UID, so it would requote on the
 * arrival of an arrangement identical to the one already held.
 */
export function ConsignmentChangeShouldTriggerQuote(item: SmartSendItemApiModel, consignment: ConsignmentApiModel) {
  const current = item.Consignment?.Parcels ?? [];
  const incoming = consignment?.Parcels ?? [];

  return (
    current.length !== incoming.length ||
    current.some((parcel, index) => dimensionsDiffer(parcel.Dimensions, incoming[index]?.Dimensions))
  );
}

export function DimensionsHaveChanged(item: SmartSendItemApiModel, dims: Dimensions) {
  return DimensionChangeShouldTriggerQuote(item, dims);
}

export function CollectionDateHasChanged(item: SmartSendItemApiModel, date: Date) {
  return item.PreferredCollectionDate != date;
}

export function StatusChangeShouldTriggerQuote(item: SmartSendItemApiModel, status: string) {
  return item.Status != status && status === 'Active';
}

export function RecipientChangeShouldTriggerQuote(item: SmartSendItemApiModel, customer: Customer) {
  return item.Recipient?.CountryIsoCode != customer?.CountryIsoCode || item.Recipient?.Postcode != customer?.Postcode;
}

export function ValueChangeShouldTriggerQuote(item: SmartSendItemApiModel, value: number) {
  return item.Value != (value || SumNumber(item.MarketplaceOrderLines.map((x) => x.Value)));
}

export function ServiceChangeShouldTriggerQuote(item: SmartSendItemApiModel, service: Service) {
  return item.Service?.Id != service?.Id;
}

export function MarketplaceOrderLinesChangeShouldTriggerQuote(
  item: SmartSendItemApiModel,
  orderLines: MarketplaceOrderApiModel[]
) {
  return item.Value != SumNumber(orderLines.map((x) => x.Value));
}

export function TagsHaveChanged(item: SmartSendItemApiModel, tags: string[]) {
  return JSON.stringify(item.Tags.sort()) != JSON.stringify((tags || []).sort());
}

export function ExtrasHaveChanged(item: ExtraBase[], extras: ExtraBase[]) {
  return (
    JSON.stringify(item.map((x) => `${x.Id}`).sort()) != JSON.stringify((extras.map((x) => `${x.Id}`) || []).sort())
  );
}

export function OrderLinesHaveChanged(_item: MarketplaceOrderApiModel[], _orderLines: MarketplaceOrderApiModel[]) {
  return true;
}

export function CustomerHasChanged(customer: Customer, newCustomer: Customer) {
  let differences = 0;
  for (const x in customer) {
    switch (x) {
      case 'Meta':
        if (MetaHasChanged(customer.Meta, newCustomer.Meta)) {
          differences++;
        }
        break;
      default:
        if (customer[x] != newCustomer[x]) {
          differences++;
        }
        break;
    }
  }

  return differences > 0;
}

export function MetaHasChanged(item: Meta, meta: Meta) {
  const i = JSON.stringify(MetaToStrings(item));
  const m = JSON.stringify(MetaToStrings(meta));
  return i != m;
}

export function ConsignmentHasChanged(item: ConsignmentApiModel, consignment: ConsignmentApiModel) {
  const i = item && JSON.stringify(item);
  const m = consignment && JSON.stringify(consignment);
  return i != m;
}

export function OwnersHaveChanged(item: Owner[], owners: Owner[]) {
  return JSON.stringify(item.map((x) => x.Id)) != JSON.stringify(owners.map((x) => x.Id));
}

export function MetaToStrings(meta: Meta): string[] {
  const existingMeta: string[] = [];
  for (const x in meta) {
    existingMeta.push(`${x}_${meta[x].Value}`);
  }
  return existingMeta.sort();
}
